import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import projectService from "@/services/project";
import { Project, ProjectFormData } from "@/types/project";
import { useState } from "react";

interface ProjectFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialData?: Project;
    mode: "create" | "edit";
    companyId: string;
}

export default function ProjectForm({
    open,
    onClose,
    onSuccess,
    initialData,
    mode,
    companyId,
}: ProjectFormProps) {
    const { toast } = useToast();
    const [formData, setFormData] = useState<ProjectFormData>(
        initialData
            ? { name: initialData.name, isActive: initialData.isActive, githubRepos: initialData.githubRepos ?? [] }
            : { name: "", isActive: true, githubRepos: [] }
    );
    const [githubReposText, setGithubReposText] = useState(
        (initialData?.githubRepos ?? []).join("\n")
    );
    const [loading, setLoading] = useState(false);

    const handleChange = (
        field: string,
        value: string | boolean
    ) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast({
                title: "Erro",
                description: "Nome do projeto é obrigatório",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);

        const payload: ProjectFormData = {
            ...formData,
            githubRepos: githubReposText
                .split("\n")
                .map((url) => url.trim())
                .filter(Boolean),
        };

        try {
            if (mode === "create") {
                const [response, error] = await projectService.create(companyId, payload);

                if (error) {
                    toast({
                        title: "Erro",
                        description: error.msg || "Erro ao criar projeto",
                        variant: "destructive",
                    });
                    return;
                }

                toast({
                    title: "Sucesso",
                    description: "Projeto criado com sucesso",
                });
            } else if (mode === "edit" && initialData) {
                const [response, error] = await projectService.update(
                    companyId,
                    initialData._id,
                    payload
                );

                if (error) {
                    toast({
                        title: "Erro",
                        description: error.msg || "Erro ao atualizar projeto",
                        variant: "destructive",
                    });
                    return;
                }

                toast({
                    title: "Sucesso",
                    description: "Projeto atualizado com sucesso",
                });
            }

            onSuccess();
            onClose();
        } catch (error) {
            toast({
                title: "Erro",
                description: "Ocorreu um erro ao processar a requisição",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>
                            {mode === "create" ? "Novo Projeto" : "Editar Projeto"}
                        </DialogTitle>
                        <DialogDescription>
                            {mode === "create"
                                ? "Preencha os dados para criar um novo projeto"
                                : "Atualize os dados do projeto"}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                                Nome
                            </Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                className="col-span-3"
                                placeholder="Nome do projeto"
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="active" className="text-right">
                                Ativo
                            </Label>
                            <div className="col-span-3 flex items-center space-x-2">
                                <Switch
                                    id="active"
                                    checked={formData.isActive}
                                    onCheckedChange={(checked) => handleChange("active", checked)}
                                />
                                <span>{formData.isActive ? "Sim" : "Não"}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 items-start gap-4">
                            <Label htmlFor="githubRepos" className="text-right pt-2">
                                Repositórios GitHub
                            </Label>
                            <Textarea
                                id="githubRepos"
                                value={githubReposText}
                                onChange={(e) => setGithubReposText(e.target.value)}
                                className="col-span-3"
                                placeholder={"https://github.com/org/repo-1\nhttps://github.com/org/repo-2"}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" type="button" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading
                                ? "Salvando..."
                                : mode === "create"
                                    ? "Criar"
                                    : "Salvar"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
} 